import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Loader2, Award, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { STATUS_ORDER, TIER_ORDER } from "@/lib/constants";
import { useLang } from "@/i18n/LanguageContext";
import { CreatorDocuments } from "@/components/admin/CreatorDocuments";
import { EuBadge } from "@/components/admin/EuBadge";

const FIELD = "w-full bg-transparent border border-aura-border rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-white/40 outline-none transition-colors";
const LABEL = "block text-xs uppercase tracking-[0.1em] text-zinc-500 mb-1.5";

export function CreatorSheet({ creator, open, onOpenChange, onSaved, onDeleted }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const { t } = useLang();

  useEffect(() => {
    if (creator) setForm({ ...creator, content_links_text: (creator.content_links || []).join("\n") });
  }, [creator]);

  if (!form) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        status: form.status, notes: form.notes || "", tracking_number: form.tracking_number || "",
        shipped_date: form.shipped_date || "", delivered_date: form.delivered_date || "",
        content_links: (form.content_links_text || "").split("\n").map((s) => s.trim()).filter(Boolean),
        sales_generated: parseFloat(form.sales_generated || 0) || 0, commissions: parseFloat(form.commissions || 0) || 0,
        discount_code: form.discount_code || "", amazon_attribution_link: form.amazon_attribution_link || "", founding_member: !!form.founding_member,
        tier: form.tier || "explorer",
      };
      const { data } = await api.patch(`/admin/creators/${form.id}`, payload);
      toast.success(t("sheet.updated"));
      onSaved(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(t("sheet.deleteConfirm"))) return;
    try { await api.delete(`/admin/creators/${form.id}`); toast.success(t("sheet.deleted")); onDeleted(form.id); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const genDiscount = async () => {
    try {
      const { data } = await api.post(`/admin/creators/${form.id}/discount-code`);
      setForm((f) => ({ ...f, discount_code: data.discount_code }));
      onSaved(data);
      toast.success(t("sheet.generated"));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="creator-sheet" className="bg-[#0a0a0a] border-aura-border text-white w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader><SheetTitle className="text-white font-display text-2xl">{form.first_name} {form.last_name}</SheetTitle></SheetHeader>

        <div className="mt-6 space-y-6">
          <div className="rounded-xl border border-aura-border bg-white/[0.02] p-4 grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-zinc-500 text-xs">{t("sheet.email")}</p><p className="text-zinc-200 break-words">{form.email}</p></div>
            <div><p className="text-zinc-500 text-xs">{t("sheet.country")}</p><p className="text-zinc-200 flex items-center gap-2">{form.country || "—"} <EuBadge country={form.country} /></p></div>
            <div><p className="text-zinc-500 text-xs">{t("sheet.followers")}</p><p className="text-zinc-200">{(form.followers || 0).toLocaleString()}</p></div>
            <div><p className="text-zinc-500 text-xs">{t("sheet.views")}</p><p className="text-zinc-200">{(form.avg_views || 0).toLocaleString()}</p></div>
            <div className="col-span-2"><p className="text-zinc-500 text-xs">{t("sheet.social")}</p>
              <p className="text-zinc-200 break-words">{[form.instagram && `IG ${form.instagram}`, form.tiktok && `TT ${form.tiktok}`, form.youtube && `YT ${form.youtube}`].filter(Boolean).join(" · ") || "—"}</p></div>
            <div className="col-span-2"><p className="text-zinc-500 text-xs">{t("sheet.address")}</p><p className="text-zinc-200">{form.address || "—"}</p></div>
            <div className="col-span-2"><p className="text-zinc-500 text-xs">{t("sheet.quest")}</p><p className="text-zinc-200">{form.owns_quest3 ? t("apply.yes") : t("apply.no")}</p></div>
            {form.about && <div className="col-span-2"><p className="text-zinc-500 text-xs">{t("sheet.about")}</p><p className="text-zinc-300">{form.about}</p></div>}
          </div>

          <div>
            <label className={LABEL}>{t("sheet.status")}</label>
            <select data-testid="sheet-status-select" className={FIELD} value={form.status} onChange={set("status")}>
              {STATUS_ORDER.map((s) => <option key={s} value={s} className="bg-[#0a0a0a]">{t(`status.${s}`)}</option>)}
            </select>
          </div>

          <div>
            <label className={LABEL}>{t("tiers.overline")}</label>
            <select data-testid="sheet-tier-select" className={FIELD} value={form.tier || "explorer"} onChange={set("tier")}>
              {TIER_ORDER.map((tr, i) => <option key={tr} value={tr} className="bg-[#0a0a0a]">{t("tiers.levels")[i]?.label} — {t("tiers.levels")[i]?.name}</option>)}
            </select>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input data-testid="sheet-founding-toggle" type="checkbox" checked={!!form.founding_member} onChange={(e) => setForm((f) => ({ ...f, founding_member: e.target.checked }))} className="h-4 w-4 accent-amber-400" />
            <span className="text-sm text-zinc-200 inline-flex items-center gap-1.5"><Award size={15} className="text-amber-400" /> {t("sheet.founding")}</span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div><label className={LABEL}>{t("sheet.sales")}</label><input data-testid="sheet-sales" type="number" className={FIELD} value={form.sales_generated ?? ""} onChange={set("sales_generated")} /></div>
            <div><label className={LABEL}>{t("sheet.commissions")}</label><input data-testid="sheet-commissions" type="number" className={FIELD} value={form.commissions ?? ""} onChange={set("commissions")} /></div>
            <div><label className={LABEL}>{t("sheet.discount")}</label>
              <div className="flex gap-2">
                <input data-testid="sheet-discount" className={FIELD} value={form.discount_code || ""} onChange={set("discount_code")} />
                <button type="button" data-testid="sheet-generate-discount" onClick={genDiscount} className="shrink-0 rounded-lg border border-white/20 text-xs text-zinc-200 hover:border-white/50 hover:text-white px-3 transition-colors">
                  {t("sheet.generate")}
                </button>
              </div>
            </div>
            <div><label className={LABEL}>{t("sheet.tracking")}</label><input data-testid="sheet-tracking" className={FIELD} value={form.tracking_number || ""} onChange={set("tracking_number")} /></div>
            <div><label className={LABEL}>{t("sheet.shippedDate")}</label><input type="date" className={FIELD} value={form.shipped_date || ""} onChange={set("shipped_date")} /></div>
            <div><label className={LABEL}>{t("sheet.deliveredDate")}</label><input type="date" className={FIELD} value={form.delivered_date || ""} onChange={set("delivered_date")} /></div>
          </div>

          <div><label className={LABEL}>{t("sheet.amazon")}</label><input data-testid="sheet-amazon" className={FIELD} value={form.amazon_attribution_link || ""} onChange={set("amazon_attribution_link")} /></div>
          <div><label className={LABEL}>{t("sheet.contentLinks")}</label><textarea rows={3} className={FIELD} value={form.content_links_text} onChange={set("content_links_text")} /></div>
          <div><label className={LABEL}>{t("sheet.notes")}</label><textarea data-testid="sheet-notes" rows={3} className={FIELD} value={form.notes || ""} onChange={set("notes")} /></div>

          <CreatorDocuments creatorId={form.id} />

          <div className="flex items-center gap-3 pt-2 pb-8">
            <button data-testid="sheet-save-btn" onClick={save} disabled={saving} className="flex-1 rounded-full bg-white text-black font-semibold py-3 hover:bg-zinc-200 active:scale-[0.99] transition-[background-color,transform] disabled:opacity-60 flex items-center justify-center gap-2">
              {saving ? <><Loader2 size={16} className="animate-spin" /> {t("sheet.saving")}</> : t("sheet.save")}
            </button>
            <button data-testid="sheet-delete-btn" onClick={remove} className="rounded-full border border-red-500/40 text-red-400 hover:bg-red-500/10 p-3 transition-colors"><Trash2 size={18} /></button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
